import React, { useMemo, useState } from 'react'
import YouTube from 'react-youtube'
import "./mdm.css"
import { MdSearch, MdClose } from 'react-icons/md'
import MdmIcon from './MdmIcon'
import MdmCardModal from './MdmCardModal'
import MdmTabs from './MdmTabs'
import { formatDuration } from './mdmUtils'

const MdmPlatformCards = ({ title, subtitle, cards, sections, active }) => {

    const [search, setSearch] = useState("")
    const [activeCard, setActiveCard] = useState(null)
    const [activeSectionTitle, setActiveSectionTitle] = useState(null)
    const [durations, setDurations] = useState({})

    const isSectioned = Array.isArray(sections)
    const flatCards = useMemo(() => (
        isSectioned ? sections.flatMap(s => s.cards) : (cards || [])
    ), [isSectioned, sections, cards])

    const videoIds = useMemo(() => (
        [...new Set(flatCards.filter(c => c.videoId).map(c => c.videoId))]
    ), [flatCards])

    const query = search.trim().toLowerCase()
    const matchesQuery = (card) => {
        if (!query) return true
        return [card.t, card.d].join(" ").toLowerCase().includes(query)
    }

    const filteredSections = useMemo(() => {
        if (!isSectioned) return []
        return sections
            .map(section => ({ ...section, cards: section.cards.filter(matchesQuery) }))
            .filter(section => section.cards.length > 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSectioned, sections, query])

    const filteredCards = useMemo(() => {
        if (isSectioned) return []
        return flatCards.filter(matchesQuery)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSectioned, flatCards, query])

    const totalShown = isSectioned
        ? filteredSections.reduce((acc, s) => acc + s.cards.length, 0)
        : filteredCards.length

    const handleOpen = (card, section) => {
        setActiveCard(card)
        setActiveSectionTitle(section ? section.title : title)
    }
    const handleClose = () => setActiveCard(null)
    const clearSearch = () => setSearch("")

    return (
        <div className="mdm-browser">
            <div className="mdm-topbar">
                <div className="mdm-topbar-titlewrap">
                    <h4>{title}</h4>
                    {subtitle && <p className="mdm-topbar-subtitle">{subtitle}</p>}
                </div>
                <div className="mdm-search">
                    <MdSearch className="mdm-search-icon" />
                    <input
                        placeholder="Search modules"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); clearSearch() } }}
                        aria-label="Search modules"
                    />
                    {search &&
                        <button
                            type="button"
                            className="mdm-search-clear"
                            onClick={clearSearch}
                            aria-label="Clear search"
                        >
                            <MdClose />
                        </button>
                    }
                </div>
            </div>

            <div className="mdm-filterrow">
                <MdmTabs active={active} />
                <span className="mdm-tally">{totalShown} of {flatCards.length} shown</span>
            </div>

            {totalShown === 0 &&
                <div className="mdm-empty">
                    <h6>No modules match "{search}"</h6>
                    <p className="mb-0 mt-2">Try a different term, or clear your search.</p>
                    <button type="button" className="mdm-empty-reset" onClick={clearSearch}>
                        Clear search
                    </button>
                </div>
            }

            {isSectioned
                ? filteredSections.map((section) => (
                    <div className="mdm-section" key={section.id}>
                        <div className="mdm-section-head">
                            <h6>{section.title}</h6>
                            <span className="mdm-section-note">{section.note}</span>
                            <span className="mdm-section-tally">{section.cards.length} {section.cards.length === 1 ? "module" : "modules"}</span>
                        </div>
                        <div className="row g-4">
                            {section.cards.map((card, cardIndex) => (
                                <div key={cardIndex} className="col-sm-12 col-md-6 col-lg-4 col-xl-3">
                                    <button
                                        className={`mdm-custom-shadow ${card.soon ? "mdm-card-soon" : ""}`}
                                        onClick={() => handleOpen(card, section)}
                                    >
                                        <div className="mdm-card-icon">
                                            <MdmIcon name={card.icon} color="rgb(32, 31, 31)" />
                                        </div>
                                        <div className="mdm-card-title">{card.t}</div>
                                        <div className="mdm-card-desc">{card.d}</div>
                                        <div className="mdm-card-foot">
                                            {card.soon
                                                ? <span className="mdm-soon-badge">Coming soon</span>
                                                : (durations[card.videoId] && <span className="mdm-mins">{durations[card.videoId]}</span>)
                                            }
                                        </div>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
                : (
                    <div className="row g-4">
                        {filteredCards.map((card, cardIndex) => (
                            <div key={cardIndex} className="col-sm-12 col-md-6 col-lg-4 col-xl-3">
                                <button
                                    className={`mdm-custom-shadow ${card.soon ? "mdm-card-soon" : ""}`}
                                    onClick={() => handleOpen(card)}
                                >
                                    <div className="mdm-card-icon">
                                        <MdmIcon name={card.icon} color="rgb(32, 31, 31)" />
                                    </div>
                                    <div className="mdm-card-title">{card.t}</div>
                                    <div className="mdm-card-desc">{card.d}</div>
                                    <div className="mdm-card-foot">
                                        {card.soon
                                            ? <span className="mdm-soon-badge">Coming soon</span>
                                            : (durations[card.videoId] && <span className="mdm-mins">{durations[card.videoId]}</span>)
                                        }
                                    </div>
                                </button>
                            </div>
                        ))}
                    </div>
                )
            }

            {activeCard &&
                <MdmCardModal card={activeCard} section={{ title: activeSectionTitle }} onClose={handleClose} />
            }

            <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                {videoIds.map(id => (
                    <YouTube
                        key={id}
                        videoId={id}
                        opts={{ height: '90', width: '160', playerVars: { autoplay: 0 } }}
                        onReady={(e) => setDurations(prev => ({ ...prev, [id]: formatDuration(e.target.getDuration()) }))}
                    />
                ))}
            </div>
        </div>
    )
}

export default MdmPlatformCards
